import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Shield,
  MapPin,
  Building2,
  Search,
  Filter,
  Grid3X3,
  List,
  Clock,
  Users,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import { useNavigate } from "react-router-dom";
import BranchCard from "../../../components/Branches/BranchCard";
import DeleteBranchModal from "../../../components/Branches/DeleteBranchModal";
import { useEffect as useEffectHook } from "react";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import PermissionAwareButton from "../../../components/Permissions/PermissionAwareButton";
import branchService from "../../../requests/branches/BranchService";

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex flex-col gap-4 justify-between items-center p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:flex-row">
    <p className="text-sm text-gray-600">
      Showing page <span className="font-medium">{currentPage}</span> of{" "}
      <span className="font-medium">{totalPages}</span>
    </p>
    <div className="flex gap-2 items-center">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-xl transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5 text-gray-600" />
      </button>
      <div className="flex gap-1 items-center">
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => onPageChange(i + 1)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium transition-colors ${
              currentPage === i + 1
                ? "bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white shadow-lg"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-xl transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5 text-gray-600" />
      </button>
    </div>
  </div>
);

const Branches = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const branchesPerPage = 6;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [filterRegion, setFilterRegion] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);

  // Permission checks
  const { canPerform, isAuthenticated, isMainVendor } = usePermissionCheck();

  // Check specific permissions
  const canViewBranches =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.VIEW_BRANCHES
    ) || isMainVendor;
  const canCreateBranches =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.CREATE_BRANCH
    ) || isMainVendor;
  const canEditBranches =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.EDIT_BRANCH
    ) || isMainVendor;
  const canDeleteBranches =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.DELETE_BRANCH
    ) || isMainVendor;

  //* Fetch branches from API
  const fetchBranches = async () => {
    setIsLoading(true);
    try {
      const response = await branchService.getBranches();
      const { data } = response;
      if (data?.status_code === "000" && data?.in_error === false) {
        setBranches(data?.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        toast.error(data?.reason || "Failed to fetch branches");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error?.response?.reason || "Failed to fetch branches");
    } finally {
      setIsLoading(false);
    }
  };

  useEffectHook(() => {
    if (canViewBranches) {
      fetchBranches();
    }
  }, [canViewBranches]);

  // Filter branches based on search and region
  const filteredBranches = branches.filter((branch) => {
    const matchesSearch =
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.region.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = !filterRegion || branch.region === filterRegion;
    return matchesSearch && matchesRegion;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBranches.length / branchesPerPage)
  );
  const currentBranches = filteredBranches.slice(
    (currentPage - 1) * branchesPerPage,
    currentPage * branchesPerPage
  );

  const handleEditBranch = (branch) => {
    if (canEditBranches) {
      navigate(`/edit-branch/${branch.branch_slug}`);
    } else {
      toast.error("You don't have permission to edit branches");
    }
  };

  const handleViewBranchDetails = (branch) => {
    navigate(`/branch-details/${branch.branch_slug}`);
  };

  const handleDeleteBranch = (branch) => {
    if (!canDeleteBranches) {
      toast.error("You don't have permission to delete branches");
      return;
    }

    setBranchToDelete(branch);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBranch = async () => {
    if (!branchToDelete) return;

    try {
      setIsDeleting(true);
      const response = await branchService.deleteBranch(
        branchToDelete.branch_slug
      );

      if (
        response.status &&
        response.data?.status_code === "000" &&
        !response.data?.in_error
      ) {
        toast.success(response.data?.reason || "Branch deleted successfully");
        fetchBranches();
        setDeleteModalOpen(false);
        setBranchToDelete(null);
      } else {
        throw new Error(response.message || "Failed to delete branch");
      }
    } catch (error) {
      console.error("Error deleting branch:", error);
      toast.error(error.message || "Failed to delete branch");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteBranch = () => {
    setDeleteModalOpen(false);
    setBranchToDelete(null);
  };

  // Get unique regions for filter
  const uniqueRegions = [...new Set(branches.map((branch) => branch.region))];

  // Access denied screen for users without view permissions
  if (!isAuthenticated || !canViewBranches) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Navbar page="Branches" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Access Restricted
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view branch management. Please
              contact your administrator to request access to the Branch
              Management module.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Required Permission:
                </span>
              </div>
              <p className="text-sm text-blue-700">
                View Branches in Branch Management
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 mt-6 font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-blue-700"
            >
              Return to Dashboard
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Navbar page="Branches" />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="px-4 py-8 mx-auto max-w-7xl">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-6 justify-between mb-8 lg:flex-row lg:items-center"
          >
            <div className="space-y-2">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-xl bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white">
                  <Building2 className="w-6 h-6" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Vendor Branches
                </h1>
              </div>
              <p className="text-gray-600">
                Manage and organize all your business locations
              </p>
            </div>

            <div className="flex gap-3 items-center">
              <PermissionAwareButton
                permission={{
                  module: PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
                  action: PERMISSIONS.ACTIONS.CREATE_BRANCH,
                }}
                fallbackForMainVendor={true}
                onClick={() => navigate("/add-branch")}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF6F6E] to-[#FFB86E] text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:from-[#FF6F6E]/90 hover:to-[#FFB86E]/90"
              >
                <Plus className="w-5 h-5" />
                <span>Add New Branch</span>
              </PermissionAwareButton>
            </div>
          </motion.div>

          {/* Search and Filter Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col gap-4 mb-8 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="flex flex-col flex-1 gap-4 sm:flex-row sm:items-center">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search branches..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="py-3 pr-4 pl-10 w-full bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Region Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <select
                  value={filterRegion}
                  onChange={(e) => setFilterRegion(e.target.value)}
                  className="pl-10 pr-8 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm appearance-none min-w-[150px]"
                >
                  <option value="">All Regions</option>
                  {uniqueRegions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "list"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4"
          >
            <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-lg bg-[#FF6F6E]/10">
                  <Building2 className="w-5 h-5 text-[#FF6F6E]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Branches</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {branches.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-lg bg-[#FF9A99]/10">
                  <MapPin className="w-5 h-5 text-[#FF9A99]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Regions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {uniqueRegions.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-lg bg-[#FF6F6E]/10">
                  <Users className="w-5 h-5 text-[#FF6F6E]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Locations</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {branches.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center">
                <div className="p-2 rounded-lg bg-[#FFB5B4]/10">
                  <Clock className="w-5 h-5 text-[#FFB5B4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Recently Added</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {branches.length}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Branches Grid/List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <AnimatePresence>
              {filteredBranches.length > 0 ? (
                <>
                  <div
                    className={`grid gap-6 ${
                      viewMode === "grid"
                        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                        : "grid-cols-1"
                    }`}
                  >
                    {currentBranches.map((branch, index) => (
                      <BranchCard
                        key={branch.id}
                        branch={branch}
                        onEdit={handleEditBranch}
                        onDelete={handleDeleteBranch}
                        onViewDetails={handleViewBranchDetails}
                        isDeleting={isDeleting}
                        canEdit={canEditBranches}
                        canDelete={canDeleteBranches}
                        canViewDetails={canViewBranches}
                        viewMode={viewMode}
                        index={index}
                      />
                    ))}
                  </div>

                  {filteredBranches.length > branchesPerPage && (
                    <div className="mt-8">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex flex-col justify-center items-center p-12 bg-white rounded-3xl border border-gray-100 shadow-sm"
                >
                  <div className="flex justify-center items-center mb-6 w-24 h-24 bg-gradient-to-r from-[#FF6F6E]/20 to-[#FFB5B4]/20 rounded-full">
                    <Building2 className="w-12 h-12 text-[#FF6F6E]" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">
                    No Branches Yet
                  </h3>
                  <p className="mb-6 max-w-md text-center text-gray-500">
                    {canCreateBranches
                      ? "You haven't added any branches to your business. Start by adding your first branch location."
                      : "You don't have permission to add branches. Please contact your administrator to request access to the Branch Management module."}
                  </p>

                  {/* Permission-aware Create Button in Empty State */}
                  <PermissionAwareButton
                    permission={{
                      module: PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
                      action: PERMISSIONS.ACTIONS.CREATE_BRANCH,
                    }}
                    fallbackForMainVendor={true}
                    onClick={() => navigate("/add-branch")}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF6F6E] to-[#FFB86E] text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:from-[#FF6F6E]/90 hover:to-[#FFB86E]/90"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add Your First Branch</span>
                  </PermissionAwareButton>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteBranchModal
        isOpen={deleteModalOpen}
        onClose={cancelDeleteBranch}
        onConfirm={confirmDeleteBranch}
        branch={branchToDelete}
        isLoading={isDeleting}
      />
    </>
  );
};

export default Branches;
